/**
 * Mapper utilities for EditChat functionality
 * Maps between GraphQL generated types and domain types from @choco-admin/types
 */

import type {
  ChatDetailsResponse,
  ChatHistoryResponse,
  ChatHistoryMessage,
  ChatHistoryMessageKind,
  ChatHistoryAttachment,
  ChatHistoryOrderDetails,
  ChatHistoryOrderProduct,
  UpdateChatInput,
  ChatStatus,
  ChatType,
} from '@choco-admin/types';

import type {
  ChatHistoryDataQuery,
  HistoryMessageFragmentFragment,
  HistoryOrderFragmentFragment,
} from '../../../generated';

// These would be the actual generated types - using any for now until GraphQL generation is complete
type GraphQLChatData = any;
type GraphQLChatUpdateInput = any;
type GraphQLSubCatalogsData = any;

// Fully-typed shapes for the chat history mapping (fed by the generated query).
type HistoryMessagesConnection = ChatHistoryDataQuery['messages'];
type HistoryMessageNode = HistoryMessageFragmentFragment;
type HistoryOrderNode = HistoryOrderFragmentFragment;

export const getSupplierEntityId = (
  chatData: Pick<ChatDetailsResponse, 'supplierEntityId' | 'supplierEntity'>
): string | undefined => chatData.supplierEntityId;

export const getSupplierProfileRoute = (
  chatData: Pick<ChatDetailsResponse, 'supplierEntityId' | 'supplierEntity'>
): string | null => {
  const supplierEntityId = getSupplierEntityId(chatData);
  return supplierEntityId ? `/suppliers/${supplierEntityId}` : null;
};

/**
 * Extracts the chat's customer-specific minimum order value from the supplier's
 * resolved `appliedMinimumOrderValue`. That value can be either the
 * supplier-level default or a customer-specific override — only the latter
 * (`type: 'customerLevel'`) is a real chat MOV. Mirrors the legacy admin so the
 * Settings form never turns an inherited default into an explicit override on
 * save. The amount is converted from minor units to a major-unit string for the
 * form input.
 */
const mapCustomerLevelMov = (
  supplierEntity: GraphQLChatData
): { amount?: string; currency?: string } | undefined => {
  const applied = supplierEntity?.appliedMinimumOrderValue;
  if (
    applied?.__typename !== 'MinimumOrderValue' ||
    applied?.type !== 'customerLevel'
  ) {
    return undefined;
  }

  const price = applied?.minimumOrderValue?.price;
  if (!price || price.amount == null) {
    return undefined;
  }

  const decimalPlaces: number = price.decimalPlaces ?? 2;
  return {
    amount: (price.amount / 10 ** decimalPlaces).toString(),
    currency: price.currency ?? undefined,
  };
};

/**
 * Helper function to map chat type from GraphQL to domain
 */
export const mapChatType = (gqlType: string): ChatType => {
  switch (gqlType?.toUpperCase()) {
    case 'ORDERING':
      return 'ORDERING';
    case 'SUPPORT':
      return 'SUPPORT';
    default:
      return 'ORDERING';
  }
};

/**
 * Helper function to map chat status from GraphQL to domain
 */
export const mapChatStatus = (gqlStatus: string): ChatStatus => {
  switch (gqlStatus?.toLowerCase()) {
    case 'active':
      return 'active';
    case 'archived':
      return 'archived';
    case 'pending':
      return 'pending';
    case 'requested':
      return 'requested';
    default:
      return 'active';
  }
};

/**
 * Maps GraphQL chat data to domain ChatDetailsResponse
 */
export const mapToChatDetailsResponse = (
  chatData: GraphQLChatData,
  subCatalogsData?: GraphQLSubCatalogsData
): ChatDetailsResponse => {
  return {
    id: chatData.id,
    chatName: chatData.chatName || '',
    restaurantName: chatData.restaurantName || '',
    address: chatData.deliveryAddress || '',
    customerNumber: chatData.customerNumber || null,
    additionalEmails: chatData.additionalEmails || [],
    isEnabled: chatData.enabled || false,
    type: mapChatType(chatData.type),
    status: mapChatStatus(chatData.status),
    // Creation context drives the requested-chat fulfilment guidance. The
    // GetChatData query exposes it via `createdBy { createdContext }`.
    createdContext: chatData.createdBy?.createdContext ?? null,
    createdAt: chatData.createdAt || new Date().toISOString(),
    updatedAt: chatData.updatedAt || new Date().toISOString(),
    // Drives the Puppy-mode access boundary on direct chat URLs; default to a
    // normal (non-puppy) chat when the backend omits the flag.
    isPuppy: chatData.isPuppy ?? false,

    // Entity references
    buyerEntity: chatData.buyer
      ? {
          id: chatData.buyer.id,
          name: chatData.buyer.name,
          referenceId: chatData.buyer.referenceId,
        }
      : null,

    supplierEntity: chatData.supplier
      ? {
          id: chatData.supplier.id,
          name: chatData.supplier.name,
          referenceId: chatData.supplier.referenceId,
        }
      : null,

    // The actual Supplier entity id (needed for catalog listing) — the
    // supplierEntity mapping above carries the bot user id, not the entity id.
    supplierEntityId: chatData.supplierEntity?.id ?? undefined,

    // Customer-specific MOV (only when the supplier resolves a customerLevel
    // override; a supplier-level default is left unset so the form treats it as
    // "no override").
    movSettings: mapCustomerLevelMov(chatData.supplierEntity),

    // Users with extended information
    users: (chatData.users?.edges || []).map((edge: any) => ({
      id: edge.node.id,
      name: edge.node.name,
      email: edge.node.email,
      phone: edge.node.phone,
      supplier: edge.node.supplier || false,
      botUser: edge.node.botUser || false,
      orderPreferences: edge.node.orderPreference
        ? {
            whatsApp: edge.node.orderPreference.whatsApp,
            sms: edge.node.orderPreference.sms,
            fax: edge.node.orderPreference.fax,
          }
        : undefined,
    })),

    // Sub-catalogs
    subCatalogs:
      subCatalogsData?.subCatalogs?.map((subCatalog: any) => ({
        id: subCatalog.id,
        name: subCatalog.name,
        supplierId: subCatalog.supplierId,
      })) || [],

    // Configuration
    priceVisibility: {
      displayPricesOverride: chatData.displayPricesOverride,
    },

    deliverySettings: chatData.deliverySettings
      ? {
          hasCustomSettings: chatData.deliverySettings.hasCustomSettings,
          deliveryDays: chatData.deliverySettings.deliveryDays,
          cutoffType: chatData.deliverySettings.cutoffType,
          cutoffTimes: chatData.deliverySettings.cutoffTimes,
        }
      : undefined,

    // Edit-specific fields
    buyerUsers: [],
    supplierUsers: [],
  };
};

/**
 * Maps domain UpdateChatInput to GraphQL input
 */
export const mapToGraphQLChatUpdateInput = (
  input: UpdateChatInput
): GraphQLChatUpdateInput => {
  return {
    chatName: input.chatName,
    restaurantName: input.restaurantName,
    deliveryAddress: input.deliveryAddress,
    customerNumber: input.customerNumber,
    additionalEmails: input.additionalEmails || [],
    buyerId: input.buyerId,
    subCatalogIds: input.subCatalogIds || [],
    enabled: input.enabled,
  };
};

/**
 * Extracts the media attachment URL, if any. `attachment` is a union and only
 * the `Media` variant carries a URL, so we narrow on the typename.
 */
const mapHistoryAttachment = (
  attachment: HistoryMessageNode['attachment']
): ChatHistoryAttachment | null => {
  if (attachment && attachment.__typename === 'Media' && attachment.url) {
    return { url: attachment.url };
  }

  return null;
};

/**
 * Maps the order's product lines, dropping any null entries the API may return.
 */
const mapHistoryOrderProducts = (
  orderProducts: HistoryOrderNode['orderProducts']
): ChatHistoryOrderProduct[] =>
  (orderProducts ?? [])
    .filter(
      (orderProduct): orderProduct is NonNullable<typeof orderProduct> =>
        orderProduct !== null
    )
    .map((orderProduct) => ({
      id: orderProduct.product.id,
      name: orderProduct.product.name,
      unit: orderProduct.product.unit ?? '',
      amount: orderProduct.amount,
    }));

/**
 * Maps the operational order context attached to an order message.
 */
const mapHistoryOrder = (order: HistoryOrderNode): ChatHistoryOrderDetails => ({
  id: order.id,
  // Order timestamps are epoch millis; normalise to an ISO string for the UI.
  createdAt: new Date(order.createdAt).toISOString(),
  referenceId: order.referenceId ?? '',
  status: order.status ?? '',
  supplierStatus: order.orderSupplierStatus ?? '',
  notes: order.body ?? '',
  sender: order.user?.name ?? '',
  supplier: order.supplier?.name ?? '',
  products: mapHistoryOrderProducts(order.orderProducts),
});

/**
 * Maps a single history message, preserving attachment and order context and
 * classifying it so the UI can render plain, media, and order messages
 * distinctly.
 */
const resolveMessageKind = (
  order: ChatHistoryOrderDetails | null,
  attachment: ChatHistoryAttachment | null
): ChatHistoryMessageKind => {
  if (order) {
    return 'order';
  }
  if (attachment) {
    return 'media';
  }
  return 'plain';
};

const mapHistoryMessage = (node: HistoryMessageNode): ChatHistoryMessage => {
  const attachment = mapHistoryAttachment(node.attachment);
  const order = node.order ? mapHistoryOrder(node.order) : null;
  const kind = resolveMessageKind(order, attachment);

  return {
    id: node.id,
    // Message timestamps are epoch millis; normalise to an ISO string.
    dateTime: new Date(node.createdAt).toISOString(),
    from: node.user?.name || 'Unknown',
    to: node.chat?.supplier?.name || 'Unknown',
    message: node.body || '',
    kind,
    attachment,
    order,
  };
};

/**
 * Maps GraphQL messages data to domain ChatHistoryResponse.
 *
 * Preserves attachment info and the full operational order context (status,
 * supplier status, reference id, notes, sender, supplier, product lines) so CS
 * can inspect chats at parity with active legacy. Keeps the query's 50-message
 * limit; no pagination.
 */
export const mapToChatHistoryResponse = (
  messagesData: HistoryMessagesConnection
): ChatHistoryResponse => {
  const messages: ChatHistoryMessage[] = (messagesData.edges ?? []).map(
    (edge) => mapHistoryMessage(edge.node)
  );

  return {
    messages,
    totalCount: messages.length,
  };
};
